import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Copy, PauseCircle, PlayCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import { toast } from "sonner";

const ALL_ROLES = [
  { v: "super_admin",         label: "Super Admin (platform-wide)" },
  { v: "customer_admin",      label: "Organization Admin (manages own org)" },
  { v: "admin",               label: "Admin (alias for customer_admin)" },
  { v: "building_manager",    label: "Building Manager" },
  { v: "site_manager",        label: "Site Manager" },
  { v: "fire_safety_officer", label: "Fire Safety Officer" },
  { v: "viewer",              label: "Viewer (read-only)" },
];
// Roles available when inviting — super_admin can invite any; admin can invite non-super roles
const inviteRolesFor = (role) =>
  role === "super_admin"
    ? ALL_ROLES
    : ALL_ROLES.filter((r) => r.v !== "super_admin");

export default function UserManagement() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "viewer" });
  const inviteRoles = inviteRolesFor(user?.role);
  const [savedTemp, setSavedTemp] = useState(null);

  const load = () => api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => toast.error("Access denied"));
  useEffect(() => { load(); }, []);

  const change = async (uid, role) => {
    try { await api.patch(`/auth/users/${uid}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error("Only super_admin can change roles"); }
  };

  const toggleSuspend = async (u) => {
    const action = u.status === "suspended" ? "activate" : "suspend";
    try {
      await api.post(`/tenant/users/${u.user_id}/suspend`, { action });
      toast.success(`User ${action === "suspend" ? "suspended" : "reactivated"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const invite = async () => {
    if (!form.email || !form.name) { toast.error("Email and name required"); return; }
    try {
      const r = await api.post("/tenant/invite", form);
      toast.success("User invited");
      setSavedTemp({ email: form.email, temp: r.data.temp_password_dev });
      setForm({ email: "", name: "", role: "viewer" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    }
  };

  const canInvite = can("user_management", "create");

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">Assign roles across Fire360Pro workspace</p>
        </div>
        {canInvite && (
          <Button onClick={() => { setSavedTemp(null); setOpen(true); }} className="f360-gradient border-0" data-testid="add-user-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add User
          </Button>
        )}
      </div>

      <div className="glass rounded-lg overflow-hidden">
        <div className="table-scroll">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Provider</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const isSuspended = u.status === "suspended";
              const isSelf = u.user_id === user?.user_id;
              return (
              <TableRow key={u.user_id} className={`border-gray-200 ${isSuspended ? "opacity-50" : ""}`} data-testid={`user-row-${u.user_id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {u.picture ? <img src={u.picture} className="w-8 h-8 rounded-full" alt="" /> : <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs text-white ${isSuspended ? "bg-gray-400" : "f360-gradient"}`}>{u.name?.[0]}</div>}
                    <div>
                      <span className="font-semibold">{u.name}</span>
                      {isSuspended && <div className="text-[10px] text-red-500 font-bold uppercase">Suspended</div>}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-gray-500">{u.email}</TableCell>
                <TableCell><Badge className="bg-gray-50 border border-gray-200 text-gray-600">{u.auth_provider}</Badge></TableCell>
                <TableCell><Badge className="f360-gradient border-0 text-white">{u.role}</Badge></TableCell>
                <TableCell>
                  <Badge className={isSuspended ? "bg-red-50 border border-red-200 text-red-600" : "bg-emerald-50 border border-emerald-200 text-emerald-600"}>
                    {isSuspended ? "Suspended" : "Active"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Select value={u.role} onValueChange={(v) => change(u.user_id, v)} disabled={user?.role !== "super_admin"}>
                      <SelectTrigger className="w-44 bg-gray-50 border-gray-200" data-testid={`role-sel-${u.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 text-gray-900">
                        {ALL_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {can("user_management", "update") && !isSelf && (
                      <Button size="sm" variant="ghost" onClick={() => toggleSuspend(u)}
                        className={`h-8 w-8 p-0 ${isSuspended ? "text-emerald-500 hover:text-emerald-600" : "text-amber-500 hover:text-amber-600"}`}
                        title={isSuspended ? "Reactivate user" : "Suspend user"}
                        data-testid={`suspend-${u.user_id}`}>
                        {isSuspended ? <PlayCircle className="w-4 h-4" strokeWidth={2.5} /> : <PauseCircle className="w-4 h-4" strokeWidth={2.5} />}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );})}

            {!users.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">No users.</TableCell></TableRow>}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Invite user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader>
            <DialogTitle>Invite a new user</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">Assign <strong className="text-orange-300">Organization Admin</strong> to give full management access to this organization.</p>
          </DialogHeader>
          {savedTemp ? (
            <div className="space-y-3">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <div className="text-xs text-emerald-300 font-bold uppercase tracking-wider mb-2">User created</div>
                <div className="text-sm">Email: <span className="font-mono">{savedTemp.email}</span></div>
                <div className="text-sm mt-1">Temp password: <span className="font-mono text-orange-300">{savedTemp.temp}</span>
                  <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard?.writeText(savedTemp.temp); toast.success("Copied"); }} className="h-6 px-2 ml-2"><Copy className="w-3 h-3" strokeWidth={2.5} /></Button>
                </div>
                <div className="text-[11px] text-gray-500 mt-2">Share this with the user. (In production, emailing is wired via Resend/SendGrid.)</div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setOpen(false); setSavedTemp(null); }} className="f360-gradient border-0">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3">
                <div><Label>Email *</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" /></div>
                <div><Label>Name *</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">
                      {inviteRoles.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={invite} className="f360-gradient border-0" data-testid="user-form-save">Send Invite</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
